import React from "react";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "../../../../../components/button";
import { Input } from "../../../../../components/input";
import { Label } from "../../../../../components/label";
import type { QuestionTypeRendererProps } from "./shared";
import type { SurveyQuestion } from "../../../types/survey.types";

type GridQuestionType = "multiple_choice_grid" | "checkbox_grid";

type GridQuestionProps<TType extends GridQuestionType> = QuestionTypeRendererProps<TType> & {
  kind: TType;
};

export function GridQuestion<TType extends GridQuestionType>({
  question,
  isPreview,
  onUpdateQuestion,
  kind,
}: GridQuestionProps<TType>) {
  const { data } = question;
  const rows = data.rows || [];
  const columns = data.columns || [];
  const updateQuestionData = (updates: Partial<typeof data>) => {
    onUpdateQuestion({
      ...question,
      data: {
        ...data,
        ...updates,
      },
    });
  };

  if (isPreview) {
    return (
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left border-r"></th>
              {columns.map((column) => (
                <th key={column.id} className="px-3 py-2 text-center border-r">
                  {column.text}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="px-3 py-2 border-r font-medium">{row.text}</td>
                {columns.map((column) => (
                  <td key={column.id} className="px-3 py-2 text-center border-r">
                    <input
                      type={kind === "multiple_choice_grid" ? "radio" : "checkbox"}
                      disabled
                      className="mx-auto"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Rows</Label>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              updateQuestionData({
                rows: [
                  ...rows,
                  {
                    id: `row${Date.now()}`,
                    text: "",
                    order: rows.length + 1,
                  },
                ],
              })
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Row
          </Button>
        </div>

        <div className="space-y-2">
          {rows.map((row, rowIndex) => (
            <div key={row.id} className="flex items-center gap-2">
              <Input
                value={row.text}
                onChange={(e) =>
                  updateQuestionData({
                    rows: rows.map((item, currentIndex) =>
                      currentIndex === rowIndex ? { ...item, text: e.target.value } : item,
                    ),
                  })
                }
                placeholder="Enter row text"
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  updateQuestionData({
                    rows: rows.filter((_, currentIndex) => currentIndex !== rowIndex),
                  })
                }
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Columns</Label>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              updateQuestionData({
                columns: [
                  ...columns,
                  {
                    id: `col${Date.now()}`,
                    text: "",
                    order: columns.length + 1,
                  },
                ],
              })
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Column
          </Button>
        </div>

        <div className="space-y-2">
          {columns.map((column, columnIndex) => (
            <div key={column.id} className="flex items-center gap-2">
              <Input
                value={column.text}
                onChange={(e) =>
                  updateQuestionData({
                    columns: columns.map((item, currentIndex) =>
                      currentIndex === columnIndex
                        ? { ...item, text: e.target.value }
                        : item,
                    ),
                  })
                }
                placeholder="Enter column text"
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  updateQuestionData({
                    columns: columns.filter((_, currentIndex) => currentIndex !== columnIndex),
                  })
                }
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {rows.length > 0 && columns.length > 0 && (
        <div className="space-y-2">
          <Label>Preview</Label>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <th className="px-3 py-2 text-left border-r"></th>
                  {columns.map((column) => (
                    <th key={column.id} className="px-3 py-2 text-center border-r">
                      {column.text}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-3 py-2 border-r font-medium">{row.text}</td>
                    {columns.map((column) => (
                      <td key={column.id} className="px-3 py-2 text-center border-r">
                        {kind === "multiple_choice_grid" ? (
                          <input type="radio" disabled className="mx-auto" />
                        ) : (
                          <input type="checkbox" disabled className="mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export const validateGridQuestion = (question: SurveyQuestion): string | null => {
  const questionText = question.data.question.trim();
  const rows = question.data.rows || [];
  const columns = question.data.columns || [];

  if (rows.length === 0) {
    return `Please add at least one row for question "${questionText}"`;
  }

  if (columns.length === 0) {
    return `Please add at least one column for question "${questionText}"`;
  }

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    if (!rows[rowIndex].text.trim()) {
      return `Please enter row text for question "${questionText}", row ${rowIndex + 1}`;
    }
  }

  for (let columnIndex = 0; columnIndex < columns.length; columnIndex += 1) {
    if (!columns[columnIndex].text.trim()) {
      return `Please enter column text for question "${questionText}", column ${columnIndex + 1}`;
    }
  }

  return null;
};

export default GridQuestion;
